import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import FrictionlessSignup, { OnAccountCreatedSuccessResult } from './FrictionlessSignup';

import { getStoredPaymentMethodInfo } from 'businessLogic/Insight/Reporting';
import BottomSheetModal from 'components/Core/Modal/BottomSheetModal';
import { useReportFrictionlessEvent } from 'components/Shared/FrictionlessLogin/useFrictionlessLoginLogs';
import SegmentIO from 'reporting/SegmentIO';
import {
  frictionlessCheckoutEmailSelector,
  frictionlessCheckoutPhoneSelector,
  isFrictionlessCheckoutCompletedSelector,
  isFrictionlessCheckoutDataInvalidSelector,
  isUserAlreadySignedUpSelector,
  isUserClosedFrictionlessOTPSelector,
} from 'store/auth/selectors';
import { authActions, refreshAuth, signOut } from 'store/auth/slice';
import { resumePendingPayment } from 'store/payment/slice';
import { getStore } from 'store/store';
import { useSelector } from 'store/utils';
import { createUserWallet, fetchWallets } from 'store/wallet/slice';

interface FrictionlessSignupContainerProps {
  skipFetchWalletsAfterOTP?: boolean;
}

const FrictionlessSignupContainer: React.FC<FrictionlessSignupContainerProps> = ({
  skipFetchWalletsAfterOTP = false,
}) => {
  const dispatch = useDispatch();
  const email = useSelector(frictionlessCheckoutEmailSelector);
  const phone = useSelector(frictionlessCheckoutPhoneSelector);
  const frictionlessLogger = useReportFrictionlessEvent();
  const shouldHide = useSelector(isFrictionlessCheckoutDataInvalidSelector);
  const isUserClosedFrictionlessOTP = useSelector(isUserClosedFrictionlessOTPSelector);
  const isFrictionlessCheckoutCompleted = useSelector(isFrictionlessCheckoutCompletedSelector);
  const isUserAlreadySignedUp = useSelector(isUserAlreadySignedUpSelector);

  const [isIdentityWidgetReady, setIsIdentityWidgetReady] = useState(false); // we're hiding the widget as long as we don't get the ready event

  useEffect(() => {
    if (!shouldHide) {
      frictionlessLogger.log({
        message: 'frictionless-signup-modal-displayed',
      });
    }
  }, [shouldHide]);

  // Cleanup effect to clear identity error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(authActions.setFrictionlessIdentityError({ showError: false }));
    };
  }, [dispatch]);

  // TEMPORARY SOLUTION: Known bug in identity service where new email users get signed up
  // despite closing OTP. This will be resolved in phase 3. For now, we sign out and refresh
  // the flow to prevent users from getting stuck in a signed-in state.
  const signOutAndRefreshFlow = useCallback(() => {
    if (!isUserAlreadySignedUp && skipFetchWalletsAfterOTP) {
      // Clear frictionless state and sign out to refresh the flow
      dispatch(authActions.setFrictionlessCheckoutClosedOTP({ isUserClosedOTP: true }));
      // @ts-ignore
      dispatch(signOut());

      return true; // Indicate that we handled the flow refresh and should exit early
    }
    return false; // Indicate that we didn't handle the flow refresh
  }, [dispatch, isUserAlreadySignedUp, skipFetchWalletsAfterOTP]);

  const handleOtpCancel = useCallback(() => {
    // Sign out user and refresh flow for new email users in subscription phase 2
    if (signOutAndRefreshFlow()) {
      return; // Exit early since we're signing out
    }

    // Standard OTP cancellation flow
    dispatch(authActions.setFrictionlessCheckoutClosedOTP({ isUserClosedOTP: true }));
    SegmentIO.frictionlessCheckoutOtpX();
  }, [dispatch, signOutAndRefreshFlow]);

  const handleIdentityWidgetComplete = useCallback(async () => {
    // Clear any identity error when completing successfully
    dispatch(authActions.setFrictionlessIdentityError({ showError: false }));

    try {
      // @ts-ignore
      await dispatch(refreshAuth());
      // @ts-ignore
      await dispatch(createUserWallet());
      dispatch(authActions.setFrictionlessCheckoutWalletCreated({ isWalletCreated: true }));

      // Only fetch wallets if not skipping (for subscription payments, we skip to preserve payment form state)
      if (!skipFetchWalletsAfterOTP) {
        // @ts-ignore
        await dispatch(fetchWallets());
      }

      // Fire frictionlessCheckoutSuccess after fetchWallets completes so wallet info is available
      const store = getStore();
      const state = store.getState();
      const storedPaymentMethodInfo = getStoredPaymentMethodInfo(
        state.wallet?.userWallets,
        state.featureFlags
      );
      await SegmentIO.frictionlessCheckoutSuccess(storedPaymentMethodInfo);
    } catch (error) {
      frictionlessLogger.trackSignupError('wallet');
      frictionlessLogger.log({
        message: 'frictionless-signup-identity-widget-complete-error',
        logLevel: 'error',
        error: error instanceof Error ? error : new Error(String(error)),
      });
      dispatch(authActions.setFrictionlessCheckoutWalletCreated({ isWalletCreated: false }));
    } finally {
      dispatch(authActions.setFrictionlessCheckoutComplete({ isCompleted: true }));
      // @ts-ignore
      dispatch(resumePendingPayment());
    }
  }, [dispatch]);

  const handleIdentityWidgetError = useCallback(
    (error: Error) => {
      if (skipFetchWalletsAfterOTP) {
        // Show error banner and stop payment process for frictionless subscriptions phase 2
        dispatch(
          authActions.setFrictionlessIdentityError({
            showError: true,
            errorMessage: error?.message || 'Verification error',
          })
        );
        return; // Stop the payment process
      }

      if (error?.message === 'INELIGIBLE_EMAIL_ERROR') {
        dispatch(authActions.setFrictionlessCheckoutComplete({ isCompleted: true }));
      }
      SegmentIO.frictionlessCheckoutError();
      frictionlessLogger.trackSignupError('identity');
      frictionlessLogger.log({
        message: 'frictionless-signup-identity-widget-error',
        logLevel: 'error',
        error,
      });
    },
    [dispatch]
  );

  const handleClose = useCallback(() => {
    // Clear any identity error when closing
    dispatch(authActions.setFrictionlessIdentityError({ showError: false }));

    // Sign out user and refresh flow for new email users in subscription phase 2
    if (signOutAndRefreshFlow()) {
      return; // Exit early since we're signing out
    }

    // Standard close flow
    dispatch(authActions.setFrictionlessCheckoutClosedOTP({ isUserClosedOTP: true }));
  }, [dispatch, signOutAndRefreshFlow]);

  const handleIdentityWidgetReady = (startedAt: Date) => {
    setIsIdentityWidgetReady(true);
    const end = Date.now();
    const executionTimeMs = Date.now() - startedAt.getTime();
    frictionlessLogger.log({
      message: 'frictionless-signup-identity-widget-ready',
      event: 'ready',
      rum: {
        executionTimeMs,
        end,
        start: startedAt.getTime(),
      },
    });
  };
  const handleAccountCreatedSuccess = (event: OnAccountCreatedSuccessResult) => {
    /**
     * Event validation may fail inside the Identity widget, and user may change phone number
     * So when account is registered, identity calls us back with the phone number
     */
    if (event && 'credentials' in event) {
      const phone = event.credentials.find((c) => c.credentialType === 'PHONE');
      if (!phone) {
        return;
      }
      const phoneNumber = phone.phoneCredential?.phoneNumber;
      if (!phoneNumber) {
        return;
      }
      dispatch(authActions.setFrictionlessCheckoutPhone({ phone: phoneNumber }));
    }
  };

  const handleDuplicateUsername = () => {
    SegmentIO.frictionlessCheckoutAccountExists();
    dispatch(authActions.setUserAlreadySignedUp());

    if (skipFetchWalletsAfterOTP) {
      // Show error banner and stop payment process for frictionless subscriptions phase 2
      dispatch(
        authActions.setFrictionlessIdentityError({
          showError: true,
          errorMessage: 'Email already exists in identity service',
        })
      );
      return; // Stop the payment process
    }
  };

  if (shouldHide || isFrictionlessCheckoutCompleted || isUserAlreadySignedUp) {
    return null;
  }

  const shouldDisplayIdentityWidget =
    !isUserAlreadySignedUp && isIdentityWidgetReady && !isUserClosedFrictionlessOTP;

  return (
    <BottomSheetModal
      hide={handleOtpCancel}
      closeIconPosition={{ right: '-10px', top: '-20px', mobileRight: '0px' }}
      isHidden={!shouldDisplayIdentityWidget}
      mobileWidth="95%"
      mobileBorderRadius="12px"
      mobileMarginBottom="10px"
    >
      <FrictionlessSignup
        email={email}
        phone={phone}
        onComplete={handleIdentityWidgetComplete}
        onError={handleIdentityWidgetError}
        onReady={handleIdentityWidgetReady}
        onOtpCancel={handleOtpCancel}
        onAccountCreatedSuccess={handleAccountCreatedSuccess}
        onClose={handleClose}
        onDuplicateUsername={handleDuplicateUsername}
      />
    </BottomSheetModal>
  );
};

export default FrictionlessSignupContainer;
